import { UseFormReturn } from 'react-hook-form';
import { ProductFormValues } from '../types';
import { cn } from '../../../utils/responsive';
import { ProductVariantEditor } from './ProductVariantEditor';
import { ProductImageEditor } from './ProductImageEditor';

interface ProductFormProps {
    form: UseFormReturn<ProductFormValues>;
    dynamicTags: string[];
    imageFields: any[];
    imageInputRef: React.RefObject<HTMLInputElement | null>;
    variantFields: any[];
    handleRemoveVariant: (index: number) => void;
    appendVariant: (variant: any) => void;
    handleImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleAddImageClick: () => void;
    removeImage: (index: number) => void;
    onOpenLightbox: (images: any[], index: number) => void;
    onSubmit: () => void;
}

export function ProductForm({
    form,
    dynamicTags,
    imageFields,
    imageInputRef,
    variantFields,
    handleRemoveVariant,
    appendVariant,
    handleImageUpload,
    handleAddImageClick,
    removeImage,
    onOpenLightbox,
    onSubmit,
}: ProductFormProps) {
    const { register, watch, setValue, formState: { errors } } = form;

    return (
        <form onSubmit={onSubmit} id="product-form">
            <div className="space-y-4 lg:space-y-6">
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            ชื่อสินค้า
                        </label>
                        <input
                            type="text"
                            {...register('name', { required: true })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-yellow-500 dark:bg-gray-700 dark:text-white"
                            placeholder="ชื่อสินค้า (ภาษาใดก็ได้)"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            คลังสินค้า (Warehouse) <span className="text-red-500">*</span>
                        </label>
                        <div className="flex gap-4">
                            {['HYB', 'PP'].map((wh) => (
                                <label key={wh} className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        value={wh}
                                        {...register('warehouse', { required: true })}
                                        className="w-4 h-4 text-yellow-600 focus:ring-yellow-500 dark:bg-gray-700 dark:border-gray-600"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">คลัง {wh}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                ราคา (บาท)
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                {...register('price', { required: true, min: 0 })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-yellow-500 dark:bg-gray-700 dark:text-white"
                                placeholder="0.00"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                ยอดขาย (แสดง)
                            </label>
                            <input
                                type="number"
                                {...register('soldCount', { min: 0, valueAsNumber: true })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-yellow-500 dark:bg-gray-700 dark:text-white"
                                placeholder="0"
                            />
                        </div>
                    </div>

                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                        <div className="flex items-center gap-3 mb-3">
                            <input
                                id="isOnSale"
                                type="checkbox"
                                {...register('isOnSale')}
                                className="w-4 h-4 text-yellow-600 bg-gray-100 border-gray-300 rounded focus:ring-yellow-500 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <label htmlFor="isOnSale" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                สินค้าลดราคา (ไม่ได้รับส่วนลดอื่นๆ)
                            </label>
                        </div>

                        {watch('isOnSale') && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    ราคาพิเศษ (บาท)
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    {...register('salePrice', {
                                        required: watch('isOnSale'),
                                        min: 0.01,
                                        validate: (value) => {
                                            if (watch('isOnSale')) {
                                                const price = watch('price');
                                                if (!value) return 'กรุณาระบุราคาพิเศษ';
                                                if (value >= price) return 'ราคาพิเศษต้องน้อยกว่าราคาปกติ';
                                            }
                                            return true;
                                        }
                                    })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-yellow-500 dark:bg-gray-700 dark:text-white"
                                    placeholder="0.00"
                                />
                                {errors.salePrice && (
                                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                                        {errors.salePrice.message as string}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            คำอธิบาย
                        </label>
                        <textarea
                            {...register('description')}
                            rows={4}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-yellow-500 dark:bg-gray-700 dark:text-white"
                            placeholder="คำอธิบายสินค้า..."
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-stone-700 dark:text-gray-300 mb-2">
                        แท็กสินค้า
                    </label>
                    <div className="flex flex-wrap gap-2">
                        {dynamicTags.map((tag) => {
                            const currentTags = watch('tags') || [];
                            const isSelected = currentTags.includes(tag);
                            return (
                                <button
                                    key={tag}
                                    type="button"
                                    onClick={() => {
                                        if (isSelected) {
                                            setValue('tags', currentTags.filter(t => t !== tag));
                                        } else {
                                            setValue('tags', [...currentTags, tag]);
                                        }
                                    }}
                                    className={cn(
                                        'px-3 py-1.5 rounded-full text-xs lg:text-sm font-medium transition-all duration-200',
                                        isSelected
                                            ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white shadow-sm'
                                            : 'bg-stone-200 dark:bg-gray-700 text-stone-700 dark:text-gray-300 hover:bg-stone-300 dark:hover:bg-gray-600'
                                    )}
                                >
                                    {tag}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <ProductImageEditor
                    form={form}
                    fields={imageFields}
                    onRemove={removeImage}
                    onUpload={handleImageUpload}
                    onAddClick={handleAddImageClick}
                    imageInputRef={imageInputRef}
                    onOpenLightbox={onOpenLightbox}
                />

                <ProductVariantEditor
                    form={form}
                    fields={variantFields}
                    onRemove={handleRemoveVariant}
                    onAppend={appendVariant}
                />
            </div>
        </form>
    );
}
